import styled, { keyframes } from 'styled-components'
import breakpoints from '../../../config/settings/breakpoints'
import { variantStyles } from '../../../components/Basics/Typography.styles'

const reveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const Container = styled.section`
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? `linear-gradient(
          135deg,
          #14201A 0%,
          #1A3328 38%,
          #2A1A1E 72%,
          #5C1424 100%
        )`
      : `linear-gradient(
          135deg,
          #E8F2EC 0%,
          #D4E8DC 40%,
          #F3E8E9 78%,
          #E8C9CE 100%
        )`};
  display: grid;
  grid-area: profile;
  grid-column: 1 / -1;
  grid-template-columns: 1fr;
  padding-block: ${({ theme }) => theme.spacing.xl};
  position: relative;

  @media ${breakpoints.device.sm} {
    grid-template-columns: 1fr minmax(0, 1280px) 1fr;
    padding-block: ${({ theme }) => theme.spacing.xxl};
  }
`

export const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column-reverse;
  gap: ${({ theme }) => theme.spacing.lg};
  grid-column: 1;
  max-width: 100%;
  padding-inline: ${({ theme }) => theme.spacing.md};
  position: relative;
  width: 100%;
  z-index: 1;

  @media ${breakpoints.device.sm} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.xxl};
    grid-column: 2;
    justify-content: space-between;
  }
`

export const TextContent = styled.div`
  animation: ${reveal} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
  max-width: 34rem;
  min-width: 0;
  text-align: center;
  width: 100%;

  > * {
    overflow-wrap: break-word;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media ${breakpoints.device.sm} {
    animation-delay: 0.06s;
    flex: 1 1 auto;
    text-align: start;
  }
`

export const Eyebrow = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.16em;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  text-transform: uppercase;
  ${({ theme }) => variantStyles({ variant: 'caption', theme })}
`

export const Name = styled.h1`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: clamp(2.5rem, 7vw, 4rem);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 0.98;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`

export const Lead = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.0625rem;
  font-weight: 400;
  line-height: 1.65;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  max-width: 30rem;

  @media ${breakpoints.device.sm} {
    margin-inline: 0;
  }
`

export const Meta = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  ${({ theme }) => variantStyles({ variant: 'caption', theme })}
  letter-spacing: 0.04em;
`

export const Actions = styled.nav`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  justify-content: center;

  @media ${breakpoints.device.sm} {
    justify-content: flex-start;
  }
`

export const ActionLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1.4;
  text-decoration: none;
  transition: color 0.25s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
`

export const PrimaryAction = styled(ActionLink)`
  border-block-end: 1px solid ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accent};
  padding-block-end: 0.1rem;

  &:hover {
    border-block-end-color: ${({ theme }) => theme.colors.accentHover};
    color: ${({ theme }) => theme.colors.accentHover};
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
  }
`

export const AvatarFrame = styled.div`
  animation: ${reveal} 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
  flex-shrink: 0;
  position: relative;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media ${breakpoints.device.sm} {
    animation-delay: 0.14s;
  }
`

export const Avatar = styled.img`
  aspect-ratio: 1;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: block;
  object-fit: cover;
  width: clamp(10rem, 34vw, 16.5rem);
`
